import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Pill, Plus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/EmptyState";
import StatusBadge from "@/components/StatusBadge";
import RefillRequestForm from "@/components/RefillRequestForm";
import { useToast } from "@/hooks/use-toast";

export default function RefillsPanel() {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [refills, setRefills] = useState([]);

  const fetchRefills = () => { API.get("/refill-requests").then(({ data }) => setRefills(data || [])).catch(() => {}); };
  useEffect(() => { if (user) fetchRefills(); }, [user]);

  const active = refills.filter(r => !["completed", "denied"].includes(r.status));
  const history = refills.filter(r => ["completed", "denied"].includes(r.status));

  // Group active requests by medication so duplicates collapse into one card
  const groups = [];
  const byMed = new Map();
  for (const r of active) {
    const k = (r.medication || "").trim().toLowerCase();
    if (byMed.has(k)) {
      byMed.get(k).earlier += 1;
    } else {
      const g = { latest: r, earlier: 0 };
      byMed.set(k, g);
      groups.push(g);
    }
  }

  if (showForm) return <RefillRequestForm onBack={() => setShowForm(false)} onSubmitted={() => { setShowForm(false); fetchRefills(); }} />;

  return (
    <div className="animate-fade-in" data-testid="refill-page">
      <div className="flex items-center justify-between gap-2 mb-4">
        <p className="text-xs text-muted-foreground">
          {groups.length > 0 ? `${groups.length} medication${groups.length === 1 ? "" : "s"} with active requests` : "Request and track medication refills"}
        </p>
        <Button size="sm" onClick={() => setShowForm(true)} className="rounded-full gap-1.5 shrink-0" data-testid="new-refill-button"><Plus className="w-4 h-4" />Request</Button>
      </div>

      {groups.length === 0 ? (
        <>
          <EmptyState icon={Pill} title="No active requests" description="Submit a refill request for your medication" action={<Button onClick={() => setShowForm(true)} className="rounded-full">Request Refill</Button>} />
          {history.length === 0 && (
            <div className="bg-accent/50 border border-border rounded-xl p-4 space-y-2" data-testid="refill-how-it-works">
              <p className="text-sm font-semibold">How refills work</p>
              <ol className="list-decimal pl-5 text-xs text-muted-foreground space-y-1 leading-relaxed">
                <li>Tap <strong>Request Refill</strong> and answer a few quick questions.</li>
                <li>Choose from your current medications or add a new one.</li>
                <li>Your provider reviews within 1–2 business days and confirms the pharmacy.</li>
                <li>You'll get a notification here when the refill is approved or denied.</li>
              </ol>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          {groups.map(g => <RefillCard key={g.latest.id} refill={g.latest} earlierCount={g.earlier} onUpdated={fetchRefills} />)}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3" data-testid="refill-history-heading">History ({history.length})</h3>
          <div className="space-y-3">
            {history.slice(0, 10).map(r => <RefillCard key={r.id} refill={r} onUpdated={fetchRefills} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function RefillCard({ refill, earlierCount = 0, onUpdated }) {
  const [marking, setMarking] = useState(false);
  const { toast } = useToast();

  const markAsFilled = async () => {
    setMarking(true);
    try {
      await API.put(`/refill-requests/${refill.id}`, { status: "completed" });
      toast({ title: "Marked as filled", description: `We'll remind you before ${refill.medication} runs out again.`, duration: 3500 });
      onUpdated?.();
    } catch {
      toast({ title: "Couldn't mark as filled", variant: "destructive" });
    } finally {
      setMarking(false);
    }
  };

  const canMarkFilled = refill.status === "approved";

  return (
    <div className="bg-card rounded-xl border border-border p-4 animate-fade-in" data-testid="refill-card">
      <div className="flex items-start justify-between mb-3">
        <div><h3 className="font-semibold text-sm">{refill.medication}</h3><p className="text-xs text-muted-foreground">{refill.strength}</p></div>
        <StatusBadge status={refill.status} />
      </div>
      {refill.staff_notes && <div className="mb-3 p-2 bg-accent border border-border rounded-lg"><p className="text-xs"><span className="font-medium">Response:</span> {refill.staff_notes}</p></div>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>Pharmacy: {refill.pharmacy_name}</span>
        {refill.requested_pickup_date && <span>Pickup: {refill.requested_pickup_date}</span>}
      </div>
      {earlierCount > 0 && (
        <p className="mt-2 text-[11px] text-muted-foreground italic" data-testid="refill-earlier-count">
          +{earlierCount} earlier request{earlierCount === 1 ? "" : "s"} for this medication
        </p>
      )}
      {canMarkFilled && (
        <div className="mt-3 pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Picked up from the pharmacy? Mark it filled to keep reminders accurate.</p>
          <Button size="sm" onClick={markAsFilled} disabled={marking} className="rounded-full gap-1.5 h-8 text-xs" data-testid="mark-refill-filled-button">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {marking ? "Saving..." : "Mark as filled"}
          </Button>
        </div>
      )}
    </div>
  );
}
